/* eslint react-hooks/exhaustive-deps: 0 */
import React, {
  useState,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';

import scrollIntoView from 'scroll-into-view';
import { SectionSmallText } from '../Section';
import * as S from './styled';
import masks from '../../utils/masks';
import Placeholder from '../Placeholder';

const Input = (
  {
    placeholder,
    value,
    invalid,
    errorMessage,
    onChange,
    zIndex = 0,
    readOnly,
    mask = masks.none,
    validations = [],
    name = '',
    className,
    inputClassName,
    msgFooter,
    disabled,
    shouldValidate = true,
    ...others
  },
  ref
) => {
  const inputRef = useRef();

  const [focused, setFocused] = useState(false);

  const validate = (shouldScroll = true) => {
    if (!shouldValidate) {
      return false;
    }
    const invalidRule = validations.find(v => v.validate(value));

    const validationState = invalidRule
      ? {
          invalid: true,
          errorMessage: invalidRule.errorMessage,
        }
      : {
          invalid: false,
          errorMessage: '',
        };

    if (validationState.invalid && shouldScroll) {
      scrollIntoView(inputRef.current);
    }

    onChange({ ...validationState, value, validated: true });
    return validationState.invalid;
  };

  useImperativeHandle(
    ref,
    () => ({
      validate,
      focus: () => inputRef.current.focus(),
    }),
    [value, validations]
  );

  return useMemo(
    () => (
      <>
        <S.InputContainer
          zIndex={zIndex}
          msgFooter={msgFooter}
          className={className}
          invalid={invalid}
        >
          <Placeholder
            className="placeholder"
            active={value || focused}
            invalid={invalid}
            disabled={disabled}
          >
            {placeholder}
          </Placeholder>
          <S.Input
            ref={inputRef}
            onBlur={() => {
              setFocused(false);
              if (!readOnly) {
                validate(false);
              }
            }}
            value={mask.mask(value)}
            invalid={invalid && !disabled}
            readOnly={readOnly}
            onChange={e => {
              onChange({
                invalid: false,
                value: mask.unmask(e.target.value),
                errorMessage: '',
              });
            }}
            name={name}
            className={inputClassName}
            disabled={disabled}
            onFocus={() => setFocused(true)}
            data-testid="input"
            {...others}
          />
          {invalid && !disabled && (
            <S.ErrorMessage>{errorMessage}</S.ErrorMessage>
          )}
          {msgFooter && (
            <SectionSmallText smaller> {msgFooter} </SectionSmallText>
          )}
        </S.InputContainer>
      </>
    ),
    [
      placeholder,
      value,
      invalid,
      zIndex,
      readOnly,
      disabled,
      focused,
      msgFooter,
      errorMessage,
    ]
  );
};
export default forwardRef(Input);
